/**
 * Expandable Voice constituency perspective — summary, per-item commentary, citations.
 * Shows Voice identity + authorship axes (source × review). Optional Mark as reviewed.
 */

import { useState } from 'react';
import { ChevronDown, Quote } from 'lucide-react';
import type { ParticipantResponse } from '../../../types/prioritization/toolSession';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useToolSessionStore } from '../../../stores/prioritization/toolSessionStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { VoiceAttribution } from './VoiceAttribution';
import { markVoiceResponseReviewedRpc } from '../voiceProvenanceRpc';
import {
  getVoiceItemCitations,
  getVoiceItemCommentary,
  isVoicePerspectiveResult,
} from '../voiceAdvisoryService';

export function VoiceContributionCard({
  response,
  defaultExpanded = false,
  compact = false,
}: {
  response: ParticipantResponse;
  defaultExpanded?: boolean;
  compact?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const userId = useUserStore((s) => s.userId);
  const subscribeToToolSession = useToolSessionStore((s) => s.subscribeToToolSession);
  const addNotification = useUIStore((s) => s.addNotification);

  const isCounted = response.countingPolicy === 'counted';

  const itemRows = response.results.filter(
    (r) => isVoicePerspectiveResult(r) || getVoiceItemCommentary(r) || (r.metadata?.rationale != null),
  );
  const legacyRows =
    itemRows.length === 0
      ? response.results.map((r) => ({
          id: r.id,
          name: r.name,
          commentary:
            typeof r.metadata?.rationale === 'string'
              ? r.metadata.rationale
              : typeof r.metadata?.commentary === 'string'
                ? r.metadata.commentary
                : null,
          citations: getVoiceItemCitations(r),
          estimate: isCounted && typeof r.score === 'number' ? r.score : null,
        }))
      : itemRows.map((r) => ({
          id: r.id,
          name: r.name,
          commentary: getVoiceItemCommentary(r),
          citations: getVoiceItemCitations(r),
          estimate: isCounted && typeof r.score === 'number' ? r.score : null,
        }));

  async function handleMarkReviewed() {
    if (!userId) return;
    const result = await markVoiceResponseReviewedRpc(response.id, userId);
    if (result.success) {
      await subscribeToToolSession(response.toolSessionId);
      addNotification({
        type: 'success',
        title: 'Marked as reviewed',
        message: 'This Voice contribution is now human-reviewed.',
        duration: 3000,
      });
    } else {
      addNotification({
        type: 'error',
        title: 'Could not mark as reviewed',
        message: result.error ?? 'Not authorized or already reviewed.',
        duration: 5000,
      });
    }
  }

  return (
    <div className="border border-electric-mustard-200 bg-electric-mustard-50/40 overflow-hidden">
      <div className="flex items-start gap-2 p-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 flex-shrink-0 text-electric-mustard-700 hover:text-electric-mustard-900"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse Voice contribution' : 'Expand Voice contribution'}
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-sm font-semibold text-[var(--text-pri)] truncate text-left hover:underline"
            >
              {response.participantName}
            </button>
            <VoiceAttribution
              source={response.creationSource ?? 'ai'}
              reviewStatus={response.creationReviewStatus ?? 'none'}
              provenanceVariant="label"
              canMarkReviewed={Boolean(userId)}
              onMarkReviewed={handleMarkReviewed}
            />
            {isCounted && (
              <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--green)] dark:text-[var(--text-ter)]">
                counted
              </span>
            )}
            {response.sponsorUserId && (
              <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-ter)]">
                via sponsor
              </span>
            )}
          </div>
          {response.rationale && (
            <p className={`mt-1 text-xs text-[var(--text-sec)] ${expanded || !compact ? '' : 'line-clamp-2'}`}>
              {response.rationale}
            </p>
          )}
          {!expanded && (
            <p className="mt-1 text-[11px] text-[var(--text-ter)]">
              {isCounted
                ? 'Counted — included in the counted aggregate (badged)'
                : 'Context only — not counted in votes'}
              {legacyRows.length > 0 ? ` · ${legacyRows.length} item${legacyRows.length === 1 ? '' : 's'}` : ''}
            </p>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-electric-mustard-100 bg-[var(--bg-raised)]/80 px-3 py-3 space-y-3">
          {response.rationale && (
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-ter)] mb-1">
                Constituency summary
              </p>
              <p className="text-sm text-[var(--text-sec)]">{response.rationale}</p>
            </div>
          )}

          {legacyRows.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-ter)]">
                Per-item commentary
              </p>
              {legacyRows.map((row) => (
                <div
                  key={row.id}
                  className="border border-[var(--rule)] bg-[var(--surface)]/80 p-2.5"
                >
                  <p className="text-xs font-medium text-[var(--text-pri)] mb-1">
                    {row.name || row.id}
                    {row.estimate != null && (
                      <span className="ml-2 font-mono text-[var(--green)] dark:text-[var(--text-ter)]">est. {row.estimate}</span>
                    )}
                  </p>
                  {row.commentary ? (
                    <p className="text-xs text-[var(--text-sec)]">{row.commentary}</p>
                  ) : (
                    <p className="text-xs text-[var(--text-ter)] italic">No commentary captured.</p>
                  )}
                  {row.citations.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {row.citations.map((c, i) => (
                        <li
                          key={`${row.id}-cite-${i}`}
                          className="flex items-start gap-1.5 text-[11px] text-[var(--text-ter)]"
                        >
                          <Quote className="h-3 w-3 mt-0.5 flex-shrink-0 text-electric-mustard-600" />
                          <span>
                            {c.title}
                            {c.externalRef ? (
                              <span className="font-mono text-sm text-[var(--text-ter)] ml-1">
                                ({c.externalRef})
                              </span>
                            ) : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          <p className="text-[11px] text-[var(--text-ter)]">
            {isCounted
              ? 'Counted Voice input — in the counted aggregate, badged; results still pass human acceptance and ratification.'
              : 'Voice context — excluded from the vote counts'}
          </p>
        </div>
      )}
    </div>
  );
}
